"use client";

import { styleDrivers } from "@/utils/componentStyle";
import { cn } from "@/utils/utils";
import {
  DropdownMenuCheckboxItem as DropdownMenuCheckboxItemPrimitive,
  DropdownMenuContent as DropdownMenuContentPrimitive,
  DropdownMenuItem as DropdownMenuItemPrimitive,
  DropdownMenuLabel as DropdownMenuLabelPrimitive,
  DropdownMenu as DropdownMenuPrimitive,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem as DropdownMenuRadioItemPrimitive,
  DropdownMenuSeparator as DropdownMenuSeparatorPrimitive,
  DropdownMenuSub,
  DropdownMenuSubContent as DropdownMenuSubContentPrimitive,
  DropdownMenuSubTrigger as DropdownMenuSubTriggerPrimitive,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { DropdownMenuItemIndicator } from "@radix-ui/react-dropdown-menu";
import * as React from "react";


// Styled wrapper components
const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContentPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuContentPrimitive>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuContentPrimitive
      ref={ref}
      sideOffset={sideOffset}
      className={cn(styleDrivers.dropdownMenu.content, className)}
      {...props}
    />
  </DropdownMenuPortal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSeparatorPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSeparatorPrimitive>
>(({ className, ...props }, ref) => (
  <DropdownMenuSeparatorPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.separator, className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSubTriggerPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSubTriggerPrimitive>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuSubTriggerPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.subTrigger, className)}
    {...props}
  >
    {children}
    <span className="ml-auto">›</span>
  </DropdownMenuSubTriggerPrimitive>
));
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSubContentPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSubContentPrimitive>
>(({ className, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuSubContentPrimitive
      ref={ref}
      className={cn(styleDrivers.dropdownMenu.subContent, className)}
      {...props}
    />
  </DropdownMenuPortal>
));
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(styleDrivers.dropdownMenu.shortcut, className)}
    {...props}
  />
);
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuItemPrimitive>
>(({ className, ...props }, ref) => (
  <DropdownMenuItemPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.item, className)}
    {...props}
  />
));
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuLabelPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuLabelPrimitive>
>(({ className, ...props }, ref) => (
  <DropdownMenuLabelPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.label, className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuCheckboxItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItemPrimitive>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuCheckboxItemPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.checkboxItem, className)}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
      <DropdownMenuItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuItemIndicator>
    </span>
    {children}
  </DropdownMenuCheckboxItemPrimitive>
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuRadioItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuRadioItemPrimitive>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuRadioItemPrimitive
    ref={ref}
    className={cn(styleDrivers.dropdownMenu.radioItem, className)}
    {...props}
  >
    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
      <DropdownMenuItemIndicator>
        <svg className="h-2 w-2 fill-current" viewBox="0 0 8 8">
          <circle cx="4" cy="4" r="4" />
        </svg>
      </DropdownMenuItemIndicator>
    </span>
    {children}
  </DropdownMenuRadioItemPrimitive>
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

// Radio option for radioGroup type
export interface RadioOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface DropdownMenuItemConfig {
  type: 'item' | 'separator' | 'label' | 'checkbox' | 'radioGroup' | 'submenu';
  label?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  disabled?: boolean;
  // For checkbox
  checked?: boolean;
  // For radioGroup
  value?: string;
  options?: RadioOption[];
  onValueChange?: (value: string) => void;
  // For submenu
  children?: DropdownMenuItemConfig[];
  // For item
  onClick?: () => void;
}

export interface DropdownMenuConfig {
  trigger?: {
    label?: string;
    icon?: React.ReactNode;
    variant?: "default" | "outline" | "ghost";
    size?: "default" | "sm" | "lg";
    iconOnly?: boolean;
  };
  items: DropdownMenuItemConfig[];
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
}

const DropdownMenuBlock: React.FC<DropdownMenuConfig> = ({
  trigger = {},
  items = [],
  align = "center",
  side = "bottom",
  sideOffset = 4,
  className,
  ...props
}) => {
  // Guard against missing required props
  if (!items || items.length === 0) {
    return null;
  }
  const renderMenuItem = (item: DropdownMenuItemConfig, index: number) => {
    switch (item.type) {
      case 'separator':
        return <DropdownMenuSeparator key={index} />;

      case 'label':
        return (
          <DropdownMenuLabel key={index}>
            {item.label || ''}
          </DropdownMenuLabel>
        );

      case 'checkbox':
        return (
          <DropdownMenuCheckboxItem
            key={index}
            checked={item.checked}
            disabled={item.disabled}
            onCheckedChange={item.onClick}
          >
            {item.label || ''}
            {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
          </DropdownMenuCheckboxItem>
        );

      case 'radioGroup':
        return (
          <React.Fragment key={index}>
            {item.label && <DropdownMenuLabel>{item.label}</DropdownMenuLabel>}
            <DropdownMenuRadioGroup
              value={item.value}
              onValueChange={item.onValueChange}
            >
              {item.options?.map((option, optIndex) => (
                <DropdownMenuRadioItem
                  key={optIndex}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </React.Fragment>
        );

      case 'submenu':
        return (
          <DropdownMenuSub key={index}>
            <DropdownMenuSubTrigger>
              {item.label || ''}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {item.children?.map((child, childIndex) => renderMenuItem(child, childIndex))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );

      case 'item':
      default:
        return (
          <DropdownMenuItem
            key={index}
            disabled={item.disabled}
            onClick={item.onClick}
          >
            {item.icon && <span className="h-4 w-4 [&>svg]:h-4 [&>svg]:w-4">{item.icon}</span>}
            <span className="flex-1">{item.label || ''}</span>
            {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
          </DropdownMenuItem>
        );
    }
  };

  const triggerClasses = cn(
    styleDrivers.dropdownMenu.trigger,
    trigger?.variant === "outline" && "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    trigger?.variant === "ghost" && "hover:bg-accent hover:text-accent-foreground",
    trigger?.size === "sm" && "h-8 px-3 text-xs",
    trigger?.size === "lg" && "h-11 px-8",
    trigger?.size === "default" && "h-9 px-4",
    trigger?.iconOnly && "p-2", // Icon-only triggers need padding
  );

  const renderTriggerContent = () => {
    if (trigger?.iconOnly && trigger?.icon) {
      return trigger.icon;
    }

    if (trigger?.icon && trigger?.label) {
      return (
        <>
          {trigger.icon}
          {trigger.label}
        </>
      );
    }

    if (trigger?.icon) {
      return trigger.icon;
    }

    return trigger?.label || '';
  };

  return (
    <DropdownMenuPrimitive {...props}>
      <DropdownMenuTrigger className={triggerClasses}>
        {renderTriggerContent()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} sideOffset={sideOffset}>
        {items.map((item, index) => renderMenuItem(item, index))}
      </DropdownMenuContent>
    </DropdownMenuPrimitive>
  );
};

export { DropdownMenuBlock as DropdownMenu };
export default DropdownMenuBlock;
